import React from "react";
import {
  Avatar,
  Box,
  Grid,
  Tooltip,
} from "@material-ui/core";
import StarIcon from "@material-ui/icons/Star";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { FaArrowRight } from "react-icons/fa";
import styled from "styled-components";
import { toast } from "react-toastify";
import { capitalizeFirstLetter } from "../../Utils";
import { VerifiedUser } from "@material-ui/icons";
import TripTimelineV2 from "../UI/TripTimelineV2";

export default function Ride({ ride, isMine = false }) {
  const navigate = useNavigate();
  const isFull = ride?.total_seats === ride?.seats_booked ? true : false;
  
  const renderTicketPrice = (price) => {
    if (isFull) {
      return <StatusBadge isFull>FULL</StatusBadge>;
    } else return `Rs. ${price}`;
  };

  const handleOnRideClick = () => {
    if (ride.status === "active" || ride.has_departed) {
      toast.info("This ride has already departed");
    } else {
      navigate(`/active-ride/${ride.id}`);
    }
  };

  const isVerified = ride?.User?.cnic_verified && ride?.User?.otp_verified;

  return (
    <CardContainer onClick={handleOnRideClick}>
      {(ride?.status === "active" || ride?.has_departed) && (
        <DepartedOverlay>
          <span>Departed</span>
        </DepartedOverlay>
      )}

      {/* Header: Date + Route */}
      <CardHeader>
        <DateText>
          {moment(ride?.departure_date).format("MMM Do, YYYY")}
        </DateText>
        <RouteText>
          {ride?.leaving_from} 
          <FaArrowRight className="arrow" /> 
          {ride?.heading_to}
        </RouteText>
      </CardHeader>

      <div style={{ padding: "0 1.5rem" }}>
         <Divider />
      </div>

      <CardBody>
        <TimelineSection>
          <TripTimelineV2
            fromTime={ride?.departure_time}
            toTime={ride?.arrival_time}
            headingTo={ride?.heading_to}
            drop={ride?.drop_location}
            pick={ride?.pickup_location}
            leavingFrom={ride?.leaving_from}
            rideId={ride?.id}
            duration={ride?.duration?.substring(0, 13)}
          />
        </TimelineSection>

        <PriceSection>
           <PriceTag>
             {renderTicketPrice(ride?.ticket)}
           </PriceTag>
           <DistanceInfo>{ride?.distance}</DistanceInfo>
           <DistanceInfo>{ride?.route?.substring(0, 20)}</DistanceInfo>
           {ride?.female_only && (
             <FemaleBadge>Female Only</FemaleBadge>
           )}
        </PriceSection>
      </CardBody>

      <CardFooter>
        <UserSection>
          <AvatarWrapper isVerified={isVerified}>
            <Avatar src={ride?.User.avatar_url} />
            {isVerified && <VerifiedIcon />}
          </AvatarWrapper>
          <UserInfo>
            <UserName>
              {capitalizeFirstLetter(ride?.User?.first_name || "User")} {capitalizeFirstLetter(ride?.User?.last_name)}
            </UserName>
            {ride?.User?.averageRating && (
              <RatingBox>
                <StarIcon style={{ color: "gold", fontSize: "14px" }} />
                <span>{ride?.User?.averageRating}</span>
              </RatingBox>
            )}
          </UserInfo>
        </UserSection>

        {!isMine && (
          <SeatsInfo>
             <VehicleName>{ride?.Vehicle?.vehicle_name?.substring(0, 20)}</VehicleName>
             <SeatsCount isFull={isFull}>
               {ride?.total_seats - ride?.seats_booked === 0 ? "No" : ride?.total_seats - ride?.seats_booked} seats left
             </SeatsCount>
          </SeatsInfo>
        )}
        
        {isMine && (
           <ActionButtons>
              <EditBtn>Edit</EditBtn>
              <DeleteBtn>Delete</DeleteBtn>
           </ActionButtons>
        )}
      </CardFooter>
    </CardContainer>
  );
}

// Styled Components

const CardContainer = styled.div`
  background: var(--card-bg);
  backdrop-filter: blur(var(--glass-blur));
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); /* Subtle shadow */
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 280px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    border-color: #054752;
  }
`;

const DepartedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  
  span {
    background: #ef4444;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 700;
    text-transform: uppercase;
  }
`;

const CardHeader = styled.div`
  padding: 1.25rem 1.5rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media(max-width: 450px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const DateText = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
`;

const RouteText = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #054752;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;

  .arrow {
    font-size: 0.9rem;
    color: #94a3b8;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f1f5f9;
  margin: 0;
`;

const CardBody = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  gap: 1rem;
  flex: 1;

  @media(max-width: 450px) {
     flex-direction: column;
  }
`;

const TimelineSection = styled.div`
  flex: 1;
`;

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 0.25rem;

  @media(max-width: 450px) {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
  }
`;

const PriceTag = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  color: #054752;
`;

const StatusBadge = styled.span`
  color: #ef4444;
  font-weight: 800;
`;

const DistanceInfo = styled.span`
  font-size: 0.8rem;
  color: #94a3b8;
`;

const FemaleBadge = styled.span`
  background: #fdf2f8;
  color: #db2777;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-weight: 600;
  margin-top: 0.5rem;
`;

const CardFooter = styled.div`
  padding: 1rem 1.5rem;
  background: #f8fafc; /* Slightly darker bottom */
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f1f5f9;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AvatarWrapper = styled.div`
  position: relative;
  
  .MuiAvatar-root {
    width: 40px;
    height: 40px;
    border: ${props => props.isVerified ? '2px solid #0ea5e9' : '2px solid transparent'};
  }
`;

const VerifiedIcon = styled(VerifiedUser)`
  position: absolute;
  bottom: -4px;
  right: -4px;
  color: #0ea5e9;
  background: white;
  border-radius: 50%;
  font-size: 1rem !important;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
`;

const RatingBox = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  
  span {
    font-size: 0.8rem;
    color: #64748b;
    font-weight: 500;
  }
`;

const SeatsInfo = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const VehicleName = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
`;

const SeatsCount = styled.span`
  font-size: 0.8rem;
  color: ${props => props.isFull ? '#ef4444' : '#10b981'};
  font-weight: 600;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const EditBtn = styled.button`
  background: transparent;
  color: #054752;
  border: 1px solid #054752;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  
  &:hover { background: #054752; color: white; }
`;

const DeleteBtn = styled.button`
  background: transparent;
  color: #ef4444;
  border: 1px solid #ef4444;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover { background: #ef4444; color: white; }
`;
